import { cn } from "@/lib/utils";

interface Callout {
  title: string;
  description: string;
  /** Position as percentage from top-left */
  x: number;
  y: number;
}

interface ImageCalloutProps {
  image: string;
  imageAlt: string;
  callouts: Callout[];
  className?: string;
}

/**
 * Image with positioned callout cards.
 * On desktop: callouts float over the image at specified positions.
 * On mobile: callouts stack below the image.
 */
const ImageCallout = ({ image, imageAlt, callouts, className }: ImageCalloutProps) => {
  return (
    <div className={cn("relative", className)}>
      {/* Image with overlays — desktop */}
      <div className="relative overflow-hidden hidden md:block">
        <img
          src={image}
          alt={imageAlt}
          className="w-full aspect-[16/9] object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-rich-black/20" />
        {callouts.map((callout, i) => (
          <div
            key={i}
            className="absolute group"
            style={{ left: `${callout.x}%`, top: `${callout.y}%` }}
          >
            {/* Pulse dot */}
            <div className="relative">
              <div className="w-3.5 h-3.5 bg-accent rounded-full shadow-lg" />
              <div className="absolute inset-0 w-3.5 h-3.5 bg-accent/50 rounded-full animate-ping" />
            </div>
            {/* Card */}
            <div
              className={cn(
                "absolute top-6 bg-card/95 backdrop-blur-sm border border-border/50 p-4 w-56 shadow-xl",
                "opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0",
                "focus-within:opacity-100 focus-within:translate-y-0",
                "transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
                i > callouts.length / 2 ? "right-0" : "left-0"
              )}
              role="tooltip"
            >
              <h4 className="font-headline text-base text-foreground mb-1">{callout.title}</h4>
              <p className="text-muted-foreground text-xs leading-relaxed">{callout.description}</p>
            </div>
            {/* Invisible button for keyboard access */}
            <button
              className="absolute inset-0 w-8 h-8 -translate-x-2 -translate-y-2 opacity-0 cursor-pointer"
              aria-label={`${callout.title}: ${callout.description}`}
              tabIndex={0}
            />
          </div>
        ))}
      </div>

      {/* Mobile fallback — stacked */}
      <div className="md:hidden space-y-0">
        <img
          src={image}
          alt={imageAlt}
          className="w-full aspect-[16/10] object-cover"
          loading="lazy"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          {callouts.map((callout, i) => (
            <div key={i} className="bg-card border border-border p-4">
              <h4 className="font-headline text-base text-foreground mb-1">{callout.title}</h4>
              <p className="text-muted-foreground text-xs leading-relaxed">{callout.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ImageCallout;
